import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { projects } from '../data/portfolio';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import styles from './ProjectPage.module.css';

/* ── Icons ── */
const ArrowLeft  = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const ArrowRight = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const External   = () => <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 10L10 2M10 2H5M10 2v5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const Github     = () => <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><path d="M7.5 1C3.91 1 1 3.91 1 7.5c0 2.87 1.86 5.3 4.44 6.16.32.06.44-.14.44-.31v-1.1c-1.8.39-2.18-.87-2.18-.87-.3-.76-.73-1-.73-1-.6-.4.04-.4.04-.4.66.05 1.01.68 1.01.68.59 1.01 1.55.72 1.93.55.06-.43.23-.72.42-.89-1.44-.16-2.95-.72-2.95-3.2 0-.71.25-1.29.68-1.74-.07-.17-.3-.83.06-1.72 0 0 .55-.18 1.8.68A6.3 6.3 0 017.5 4c.56 0 1.12.07 1.65.22 1.24-.86 1.8-.68 1.8-.68.36.89.13 1.55.06 1.72.43.45.68 1.03.68 1.74 0 2.49-1.52 3.04-2.96 3.2.23.2.44.6.44 1.2v1.79c0 .17.11.37.44.31A6.502 6.502 0 0014 7.5C14 3.91 11.09 1 7.5 1z" fill="currentColor"/></svg>;
const ImgIcon    = () => <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="3" y="5" width="22" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.2"/><circle cx="9.5" cy="11.5" r="2.5" stroke="currentColor" strokeWidth="1.1"/><path d="M3 18l7-6 5 5 3-2.5 7 6.5" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/></svg>;

/* ── Polaroid frame with real image support ── */
function Polaroid({ src, label, index, color }) {
  const rots = [-2.8, 1.6, -1.4, 2.2, -0.8, 1.9];
  const rot  = rots[index % rots.length];
  return (
    <div className={styles.polaroid} style={{ '--rot': `${rot}deg` }}>
      <div className={styles.polaroidImg} style={{ background: color || 'var(--paper3)' }}>
        {src
          ? <img src={src} alt={label} className={styles.polaroidPhoto} />
          : (
            <div className={styles.polaroidEmpty}>
              <ImgIcon />
              <span>Add screenshot</span>
              <code>{`images[${index}].src`}</code>
            </div>
          )
        }
      </div>
      <p className={styles.polaroidCaption}>{label}</p>
    </div>
  );
}

/* ── Story step ── */
function StoryStep({ num, title, text }) {
  return (
    <div className={styles.step}>
      <div className={styles.stepNum}>{num}</div>
      <div className={styles.stepBody}>
        <h3 className={styles.stepTitle}>{title}</h3>
        <p className={styles.stepText}>{text}</p>
      </div>
    </div>
  );
}

/* ── Browse more card ── */
function BrowseCard({ project, dir }) {
  const isRight = dir === 'next';
  return (
    <Link to={`/project/${project.id}`} className={`${styles.browseCard} ${isRight ? styles.browseCardRight : ''}`}>
      <div className={styles.browseDir}>
        {!isRight && <ArrowLeft />}
        <span>{isRight ? 'Next project' : 'Prev project'}</span>
        {isRight && <ArrowRight />}
      </div>
      <div className={styles.browseName}>{project.name}</div>
      <div className={styles.browseSub}>{project.subtitle}</div>
      <span className={`status-badge status-${project.status}`}>{project.statusLabel}</span>
    </Link>
  );
}

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
export default function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const project      = projects.find(p => p.id === id);
  const idx          = projects.findIndex(p => p.id === id);
  const prevProject  = projects[idx - 1] || null;
  const nextProject  = projects[idx + 1] || null;
  const otherProjects = projects.filter(p => p.id !== id).slice(0, 3);

  // Scroll reveal refs
  const heroRef    = useRef(null);
  const storyRef   = useRef(null);
  const photosRef  = useRef(null);
  const techRef    = useRef(null);
  const browseRef  = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    const refs = [heroRef, storyRef, photosRef, techRef, browseRef];
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add(styles.visible); obs.unobserve(e.target); } });
    }, { threshold: 0.06 });
    refs.forEach(r => r.current && obs.observe(r.current));
    return () => obs.disconnect();
  }, [id]);

  if (!project) return (
    <div className={styles.notFound}>
      <p>Project not found.</p>
      <Link to="/">← Back home</Link>
    </div>
  );

  const accentColor = project.accentColor || 'var(--accent)';
  const imageSlots  = [0,1,2].map(i => ({
    src:   project.images?.[i]?.src   || null,
    label: project.images?.[i]?.label || ['Overview','Interface','Detail','Workflow'][i],
    color: project.images?.[i]?.color || ['#e8f0e8','#e8e8f0','#f0e8e0','#ede8f0'][i],
  }));

  return (
    <>
      <Navbar />
      <main className={styles.page}>

        {/* ── BREADCRUMB ── */}
        <div className={styles.breadcrumb}>
          <Link to="/" className={styles.backLink}><ArrowLeft /><span>All projects</span></Link>
          <span className={styles.breadSep}>/</span>
          <span className={styles.breadCurrent}>{project.name}</span>
        </div>

        {/* ══ HERO ══ */}
        <section ref={heroRef} className={`${styles.heroSection} ${styles.fadeUp}`}>
          {/* Paper stack */}
          <div className={styles.heroStack}>
            <div className={styles.heroLeaf3} />
            <div className={styles.heroLeaf2} />
            <div className={styles.heroLeaf1} />
            <div className={styles.heroCard}>

              {/* Top row: title + actions */}
              <div className={styles.heroInner}>
                <div className={styles.heroLeft}>
                  <div className={styles.heroTopMeta}>
                    <span className={`status-badge status-${project.status}`}>{project.statusLabel}</span>
                    <span className={styles.heroYear}>{project.year}</span>
                    <span className={styles.heroIndex}>{String(idx + 1).padStart(2,'0')} / {String(projects.length).padStart(2,'0')}</span>
                  </div>
                  <h1 className={styles.heroTitle}>{project.name}</h1>
                  <p className={styles.heroSub}>{project.subtitle}</p>
                  <p className={styles.heroDesc}>{project.fullDesc || project.desc}</p>
                </div>

                <div className={styles.heroRight}>
                  <div className={styles.heroBtns}>
                    {project.liveUrl && (
                      <a href={project.liveUrl} target="_blank" rel="noreferrer" className="btn btn-dark">
                        <External /> Live demo
                      </a>
                    )}
                    {project.codeUrl && (
                      <a href={project.codeUrl} target="_blank" rel="noreferrer" className="btn btn-outline">
                        <Github /> Source code
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className={styles.tagRow}>
                {project.tags.map(t => <span key={t} className={styles.tag}>{t}</span>)}
              </div>
            </div>
          </div>

          {/* Metrics row */}
          {project.metrics && (
            <div className={styles.metricsRow}>
              {project.metrics.map(m => (
                <div key={m.label} className={styles.metric}>
                  <div className={styles.metricNum} style={{ color: accentColor }}>{m.num}</div>
                  <div className={styles.metricLabel}>{m.label}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ══ STORY ══ */}
        <section ref={storyRef} className={`${styles.storySection} ${styles.fadeUp}`}>
          <div className={styles.sectionLabel}>
            <span className={styles.labelLine} />
            <span className={styles.labelText}>the story</span>
            <span className={styles.labelLine} />
          </div>

          <div className="stack">
            <div className="stack-inner">
              <div className={styles.storyCard}>
                <StoryStep num="01" title="The Problem" text={project.problem} />
                <div className={styles.stepDivider} />
                <StoryStep num="02" title="The Solution" text={project.solution} />
                <div className={styles.stepDivider} />
                <StoryStep num="03" title="The Outcome"  text={project.outcome} />
              </div>
            </div>
          </div>
        </section>

        {/* ══ SCREENSHOTS ══ */}
        <section ref={photosRef} className={`${styles.photosSection} ${styles.fadeUp}`}>
          <div className={styles.sectionLabel}>
            <span className={styles.labelLine} />
            <span className={styles.labelText}>screenshots</span>
            <span className={styles.labelLine} />
          </div>

          <div className={styles.polaroidGrid}>
            {imageSlots.map((img, i) => (
              <Polaroid key={i} index={i} src={img.src} label={img.label} color={img.color} />
            ))}
          </div>
        </section>

        {/* ══ TECH STACK ══ */}
        <section ref={techRef} className={`${styles.techSection} ${styles.fadeUp}`}>
          <div className={styles.sectionLabel}>
            <span className={styles.labelLine} />
            <span className={styles.labelText}>tech stack</span>
            <span className={styles.labelLine} />
          </div>

          <div className={`stack ${styles.techStackWrap}`}>
            <div className="stack-inner">
              <div className={styles.techGrid}>
                {project.tags.map((t, i) => (
                  <div key={t} className={styles.techTile}>
                    <span className={styles.techTileNum}>{String(i+1).padStart(2,'0')}</span>
                    <span className={styles.techTileName}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══ BROWSE MORE ══ */}
        <section ref={browseRef} className={`${styles.browseSection} ${styles.fadeUp}`}>
          <div className={styles.sectionLabel}>
            <span className={styles.labelLine} />
            <span className={styles.labelText}>browse more projects</span>
            <span className={styles.labelLine} />
          </div>

          {/* Prev / Next navigation */}
          <div className={styles.prevNextRow}>
            {prevProject
              ? <BrowseCard project={prevProject} dir="prev" />
              : <div className={styles.browseEmpty} />
            }
            <Link to="/#projects" className={styles.allBtn} onClick={e => {
              e.preventDefault();
              navigate('/');
              setTimeout(() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }), 80);
            }}>
              ↩ All projects
            </Link>
            {nextProject
              ? <BrowseCard project={nextProject} dir="next" />
              : <div className={styles.browseEmpty} />
            }
          </div>

          {/* Other project cards */}
          <div className={styles.otherGrid}>
            {otherProjects.map(p => (
              <Link key={p.id} to={`/project/${p.id}`} className={styles.otherCard}>
                <div className={styles.otherTop}>
                  <span className={`status-badge status-${p.status}`}>{p.statusLabel}</span>
                  <span className={styles.otherYear}>{p.year}</span>
                </div>
                <div className={styles.otherName}>{p.name}</div>
                <div className={styles.otherSub}>{p.subtitle}</div>
                <div className={styles.otherTags}>
                  {p.tags.slice(0,3).map(t => <span key={t} className={styles.otherTag}>{t}</span>)}
                </div>
                <div className={styles.otherArrow}><ArrowRight /></div>
              </Link>
            ))}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
